import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    addReaction,
    createReply,
    deleteMessage,
    listReplies,
    removeReaction,
    updateMessage,
} from '../../../service/ChatService';
import {mergeMessages, reconcileMessagePage} from '../chatUtils';
import {highestServerSequence, isServerMessage} from '../serverMessages';
import useMessageRead from './useMessageRead';
import useMessageNavigation from './useMessageNavigation';
import useMessageOutbox from './useMessageOutbox';
import useMessageMutations from './useMessageMutations';
import {createRefreshQueue} from '../refreshQueue';

export default function useThreadReplies({active = true, conversation, root, currentUser, invalidation, messageMutationEvents, onRootChanged, onForbidden, cacheScope}) {
    const [replies, setReplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [initialSnapshotOwner, setInitialSnapshotOwner] = useState(null);
    const repliesRef = useRef([]);
    const timelineRef = useRef(null);
    const timelineNearBottomRef = useRef(true);
    const refreshRequestRef = useRef({generation: 0, controller: null});
    const conversationId = conversation.id;
    const rootId = root.id;
    const owner = useMemo(() => ({key: `${currentUser?.id || 0}:${conversationId}:${rootId}`, mounted: true}), [currentUser?.id, conversationId, rootId]);
    const activeScope = useRef(owner);
    activeScope.current = owner;
    useEffect(() => { owner.mounted = true; return () => { owner.mounted = false; }; }, [owner]);
    const outbox = useMessageOutbox({conversation, currentUser, rootId, onForbidden, confirmedMessages: replies, cacheScope});
    const sendOutbox = outbox.send;
    const fetchPage = useCallback(options => listReplies(conversationId, rootId, options), [conversationId, rootId]);
    const navigation = useMessageNavigation({owner, fetchPage, messagesRef: repliesRef, setMessages: setReplies, setHasMore, setError, timelineRef});
    const {activeRef: historyActive, epoch: historyEpoch, returnToLatest} = navigation;
    const acknowledge = useMessageRead(owner.key, conversationId, currentUser?.id, setError, active);
    const markRead = useCallback(async message => {
        const receipt = await acknowledge(message);
        if (!receipt || !owner.mounted || activeScope.current !== owner) return;
        setReplies(current => current.map(item => item.id === message.id ? {...item, read: true} : item));
        // A refresh failure must never retry a successful read write.
        try { await onRootChanged?.(); } catch (readError) { if (owner.mounted) setError(readError); }
    }, [acknowledge, onRootChanged, owner]);

    useEffect(() => {
        repliesRef.current = replies;
    }, [replies]);

    const refresh = useCallback(async (preserve = true, signal) => {
        if (historyActive.current) return [];
        const navigationEpoch = historyEpoch.current;
        const generation = refreshRequestRef.current.generation + 1;
        refreshRequestRef.current.controller?.abort();
        const controller = new AbortController();
        const abort = () => controller.abort(signal.reason);
        signal?.addEventListener('abort', abort, {once: true});
        if (signal?.aborted) abort();
        refreshRequestRef.current = {generation, controller};
        try {
            const payload = await listReplies(conversationId, rootId, {afterSequence: preserve ? highestServerSequence(repliesRef.current) : 0, signal: controller.signal});
            if (controller.signal.aborted || activeScope.current !== owner || !owner.mounted || refreshRequestRef.current.generation !== generation) return null;
            if (historyActive.current || historyEpoch.current !== navigationEpoch) return [];
            const incoming = payload.messages || [];
            setReplies(current => preserve ? mergeMessages(current, incoming) : reconcileMessagePage(current, incoming));
            if (!preserve) setHasMore(payload.has_more_before ?? incoming.length === 50);
            return incoming;
        } catch (refreshError) {
            if (controller.signal.aborted || refreshError?.name === 'AbortError') return null;
            throw refreshError;
        } finally {
            signal?.removeEventListener('abort', abort);
            if (refreshRequestRef.current.generation === generation) {
                refreshRequestRef.current.controller = null;
            }
        }
    }, [conversationId, rootId, owner, historyActive, historyEpoch]);

    useEffect(() => () => refreshRequestRef.current.controller?.abort(), []);

    const revealLatest = useCallback(() => new Promise(resolve => {
        window.requestAnimationFrame(() => {
            const timeline = timelineRef.current;
            if (timeline && !historyActive.current) timeline.scrollTop = timeline.scrollHeight;
            timelineNearBottomRef.current = true;
            resolve();
        });
    }), [historyActive]);

    useEffect(() => {
        let active = true;
        const initialQueue = createRefreshQueue(async (_, signal) => {
            const messages = await refresh(false, signal);
            if (!active || !messages) return;
            setInitialSnapshotOwner(owner);
            setError(null); setLoading(false);
            return revealLatest();
        }, () => true, {retry: true});
        setLoading(true);
        setInitialSnapshotOwner(null);
        setReplies([]);
        repliesRef.current = [];
        setError(null);
        void initialQueue.request(true).catch(loadError => { if (active && loadError?.name !== 'AbortError') setError(loadError); }).finally(() => active && setLoading(false));
        return () => { active = false; initialQueue.cancel(); refreshRequestRef.current.controller?.abort(); };
    }, [refresh, revealLatest, owner]);

    const eventQueue = useMemo(() => createRefreshQueue(async (_, signal) => {
        const wasNearBottom = timelineNearBottomRef.current;
        const messages = await refresh(true, signal);
        if (!messages) return;
        const through = highestServerSequence(messages);
        if (through && wasNearBottom && document.visibilityState === 'visible') await revealLatest();
        if (messages.length === 50) queueMicrotask(() => { void eventQueue.request(true).catch(setError); });
    }, () => true, {minIntervalMs: 200, retry: true}), [refresh, revealLatest]);
    useEffect(() => () => eventQueue.cancel(), [eventQueue]);
    useEffect(() => { if (initialSnapshotOwner === owner && invalidation) void eventQueue.request(true).catch(setError); }, [invalidation, eventQueue, initialSnapshotOwner, owner]);

    const refreshChangedMessage = useMessageMutations({events: messageMutationEvents, messages: replies, messagesRef: repliesRef, setMessages: setReplies, setError, owner, conversationId, actorId: currentUser?.id});

    const loadOlder = useCallback(async () => {
        const navigationEpoch = historyEpoch.current;
        const first = replies.find(isServerMessage)?.sequence;
        if (!first) return;
        const timeline = timelineRef.current;
        const oldHeight = timeline?.scrollHeight || 0;
        try {
            const payload = await listReplies(conversationId, rootId, {beforeSequence: first});
            if (activeScope.current !== owner || !owner.mounted || historyEpoch.current !== navigationEpoch) return;
            const older = payload.messages || [];
            setReplies(current => mergeMessages(older, current));
            setHasMore(payload.has_more_before ?? older.length === 50);
            window.requestAnimationFrame(() => {
                if (timeline) timeline.scrollTop += timeline.scrollHeight - oldHeight;
            });
        } catch (loadError) {
            if (activeScope.current === owner && owner.mounted) setError(loadError);
        }
    }, [conversationId, rootId, replies, owner, historyEpoch]);

    const sendReply = useCallback(async (body, attachments = [], quotedMessage = null, retryId, originalBody) => {
        const clientId = retryId || crypto.randomUUID();
        const result = await sendOutbox({body, attachments, quotedMessage, clientId, originalBody}, () => createReply(
            conversationId, rootId, body, clientId, attachments.map(attachment => attachment.id), quotedMessage?.id || null, originalBody,
        ));
        if (!result) return null;
        if (historyActive.current) { await returnToLatest(); return result; }
        setReplies(current => mergeMessages(current, [result.message]));
        return result;
    }, [conversationId, rootId, sendOutbox, historyActive, returnToLatest]);

    const changeMessage = useCallback(async (message, action) => {
        await action();
        await refreshChangedMessage(message.id);
    }, [refreshChangedMessage]);

    const actions = useMemo(() => ({
        edit: (message, body) => changeMessage(message, () => updateMessage(message.id, body, message.version)),
        remove: message => changeMessage(message, () => deleteMessage(message.id)),
        react: (message, emoji, reacted) => changeMessage(message, () => reacted ? removeReaction(message.id, emoji) : addReaction(message.id, emoji)),
        retry: message => sendReply(message.body, message.attachments || [], message.quoted_message || null, message.client_message_id, message.original_body),
    }), [changeMessage, sendReply]);

    const visibleReplies = useMemo(() => [...replies, ...outbox.messages], [replies, outbox.messages]);
    return {
        navigation,
        replies: visibleReplies,
        storageWarning: outbox.warning,
        loading,
        hasMore,
        error,
        timelineRef,
        onTimelineScroll: event => {
            const element = event.currentTarget;
            timelineNearBottomRef.current = element.scrollHeight - element.scrollTop - element.clientHeight < 96;
        },
        loadOlder,
        sendReply,
        actions,
        markRead,
    };
}
